import cx from 'classnames';
import format from 'date-fns/format';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useHistory,
  useRouteMatch,
  useParams,
  useLocation,
} from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
  Alert as MUIAlert,
  AlertTitle as MUIAlertTitle,
  Skeleton,
} from '@material-ui/lab';
import queries from '@nc-aftermarket/config/queryDefs';
import { ReactComponent as WatchIcon } from '../assets/icons/star.svg';
import AddToCartButton from '../components/AddToCartButton';
import AppDialog from '../components/AppDialog';
import Badge from '../components/Badge';
import DomainMetrics from '../components/DomainMetrics';
import Alert from '../components/Alert';
import Loader from '../components/Loader';
import Meta from '../components/Meta';
import Modal, { ModalTitle } from '../components/Modal';
import SaleCarousel from '../components/SaleCarousel';
import TextInput from '../components/TextInput';
import formatCurrency from '../lib/formatCurrency';
import getAuthRedirectUrl from '../lib/getAuthRedirectUrl';
import getSalePresenter from '../lib/getSalePresenter';
import isAuthError from '../lib/isAuthError';
import useCountdown from '../lib/useCountdown';
import useLocalStorage from '../lib/useLocalStorage';
import useUserInfo from '../lib/useUserInfo';
import useWaitCursor from '../lib/useWaitCursor';
import useWatchMutation from '../lib/useWatchMutation';
import partyPopper from '../assets/party-popper_1f389.png';

const bidLimit = 100;

export default function Sale() {
  const parent = useRef();
  const history = useHistory();
  const params = useParams();
  const match = useRouteMatch();
  const userInfo = useUserInfo();
  const [incrementViewCount] = useMutation(queries.IncrementSaleViewCount);
  const [watch, watchQuery] = useWatchMutation();
  const saleQuery = useQuery(
    params.bindomain ? queries.SalePageBIN : queries.SalePage,
    params.bindomain
      ? {
          variables: {
            domain: params.bindomain,
          },
        }
      : {
          fetchPolicy: 'cache-and-network',
          pollInterval: 5000,
          variables: {
            bidLimit,
            id: params.slug || params.id,
            idType: params.slug ? 'domain' : 'id',
          },
        },
  );

  const fragment = params.id
    ? saleQuery.client.readFragment({
        id: `Sale:${params.id}`,
        fragment: gql`
          fragment SalePageLocalCacheData on Sale {
            endDate
            id
            minBid
            price
            saleType
            product {
              name
            }
          }
        `,
      })
    : null;

  const resolvedId = saleQuery.data?.sale?.id || null;

  const presenter = getSalePresenter({
    ...fragment,
    ...saleQuery.previousData?.sale,
    ...saleQuery.data?.sale,
    ...saleQuery.data?.binSale,
  });

  // Increment sale view count when successfully loaded
  useEffect(() => {
    if (resolvedId) {
      incrementViewCount({
        variables: { id: resolvedId },
      }).catch(() => {
        // Do nothing
      });
    }
  }, [incrementViewCount, resolvedId]);

  useEffect(() => {
    if (saleQuery.data && presenter?.id && match.url !== presenter.permalink) {
      history.replace(presenter.permalink);
    }
  }, [history, match, presenter, saleQuery]);

  if (saleQuery.error && !saleQuery.data) {
    return (
      <div className="tw-wrap tw-my-4">
        <Alert error={saleQuery.error} />
      </div>
    );
  }

  if (saleQuery.data?.sale === null) {
    return (
      <div className="tw-wrap tw-my-4">
        <Meta title="Not Found - 404" />
        <Alert error={new Error('Sale not found')} severity="warning" />
      </div>
    );
  }

  if (!fragment && !saleQuery.data && saleQuery.loading) {
    return <Loader />;
  }

  const watching = userInfo.watching?.includes(presenter.name);

  return (
    <>
      <Meta
        description="Make a bid for your dream domain now. It’s easy to do, just enter the amount you want to offer and place your bid today."
        title={`${presenter.name} is listed for sale - ${
          presenter.saleType === 'auction'
            ? 'Place your bids now'
            : 'Buy this domain now'
        } - Namecheap`}
      />
      <div className="tw-wrap tw-wrap--bleed-below-sm" ref={parent}>
        <div className="sm:tw-paper tw-p-4 sm:tw-p-10 lg:tw-p-8 xl:tw-p-10 tw-mb-10 sm:tw-mt-6">
          <nav className="tw-flex tw-items-center tw-uppercase tw-mt-4 md:tw-mt-0 tw-text-sm tw-font-bold">
            <div className="tw-flex-auto tw-leading-none">
              <Link to="/">Market</Link>
              <span className="tw-mx-2">/</span>
              {presenter.saleType === 'auction' ? (
                <Link to="/auctions/">Auctions</Link>
              ) : (
                <Link to="/buynow/">Buy Now</Link>
              )}
            </div>
            <div className="tw-hidden lg:tw-block tw-text-gray-600">
              Sale ID:{' '}
              <span className="tw-ml-1 tw-font-mono tw-normal-case tw-font-light">
                {presenter.id}
              </span>
            </div>
          </nav>
          <h1
            className="
                  tw-mt-6 sm:tw-mt-4 tw-mb-8
                  tw-text-4xl sm:tw-text-5xl tw-text-gray-700 tw-font-bold tw-leading-tight tw-break-all">
            <span className="tw-mr-2">{presenter.nameFormatted}</span>
            <button
              aria-label="Watch"
              className="tw-inline-flex tw-items-center tw-mr-2"
              onClick={async () => {
                try {
                  await watch({
                    [watching ? 'remove' : 'add']: [presenter.name],
                  });
                } catch (error) {
                  window.alert(
                    `Failed to ${watching ? 'unwatch' : 'watch'} domain.`,
                  );
                }
              }}
              type="button">
              <WatchIcon
                className={cx(
                  'tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6 tw-text-gray-500',
                  'hover:tw-text-orange',
                  {
                    'tw-text-orange tw-fill-current': watching,
                    'tw-cursor-wait': watchQuery.loading,
                  },
                )}
              />
            </button>
            {presenter.statusFormatted && (
              <Badge
                className={cx(
                  'tw-text-white tw-align-middle tw-text-sm',
                  presenter.status === 'sold' ? 'tw-bg-blue' : 'tw-bg-orange',
                )}>
                {presenter.statusFormatted}
              </Badge>
            )}
          </h1>
          <div className="lg:tw-flex">
            <div className="tw-w-full lg:tw-w-1/2 lg:tw-pr-4">
              {presenter.mostRecentSale &&
              presenter.mostRecentSale.id !== presenter.id ? (
                <MUIAlert className="tw-mb-4" severity="info">
                  <MUIAlertTitle>
                    Another auction is currently in progress for this domain
                  </MUIAlertTitle>
                  This page is for an auction that has already ended.{' '}
                  <Link className="tw-underline" to={`/${presenter.name}`}>
                    Click here to go to the current active auction
                  </Link>
                </MUIAlert>
              ) : null}
              {saleQuery.error && (
                <Alert className="tw-mb-4" error={saleQuery.error} />
              )}
              <div className="tw-text-gray-700 md:tw-bg-gray-100 tw-rounded-lg">
                {presenter.endDate && (
                  <div className="tw-flex md:tw-p-6 tw-pb-6">
                    <div className="xl:tw-flex tw-flex-auto">
                      <div className="tw-w-full xl:tw-w-32 tw-mb-2 tw-leading-snug">
                        Time left
                      </div>
                      <div className="tw-flex-auto">
                        <SaleCountdown
                          pause={saleQuery.error}
                          to={
                            presenter.status === 'active'
                              ? presenter.endDate ?? Date.now()
                              : Date.now()
                          }
                        />
                        <div className="tw-mt-5 tw-text-xs">
                          Sale {presenter.isEnded ? 'ended' : 'ends'} on{' '}
                          {format(new Date(presenter.endDate), 'PPPppp')}
                          {presenter.endDateExtendedMs > 0 &&
                            ` (extended by ${
                              presenter.endDateExtendedMs / (1000 * 60)
                            } min)`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!saleQuery.error && !presenter.isEnded && (
                  <div className="md:tw-p-6 tw-pb-6 tw-border-t tw-border-white">
                    <div className="xl:tw-flex">
                      <div className="tw-w-full xl:tw-w-32 tw-mb-2 tw-leading-snug">
                        {presenter.saleType === 'auction'
                          ? 'Minimum bid'
                          : 'Price'}
                      </div>
                      <div className="tw-flex-auto tw-text-3xl tw-font-medium">
                        {presenter.saleType === 'auction'
                          ? presenter.minBidFormatted
                          : presenter.priceFormatted}
                      </div>
                    </div>
                    {presenter.saleType === 'auction' &&
                      presenter.status === 'active' &&
                      userInfo.status !== 'banned' && (
                        <SaleBidForm sale={presenter} />
                      )}
                  </div>
                )}
                {((presenter.saleType === 'buy' &&
                  !presenter.isEnded &&
                  userInfo.status !== 'banned') ||
                  presenter.selfHasActiveInvoice) && (
                  <div className="md:tw-px-4 tw-py-6 tw-border-t tw-border-white">
                    {presenter.selfHasActiveInvoice && (
                      <div className="tw-mt-6 tw-mb-10 tw-text-center">
                        <div className="tw-text-xl tw-font-bold tw-inline-flex">
                          <img
                            alt=""
                            className="tw-w-6 tw-h-6 tw-text-2xl tw-mr-2"
                            src={partyPopper}
                          />
                          Congrats, you won!
                        </div>
                        <p>
                          You may now purchase this domain and take ownership.
                        </p>
                      </div>
                    )}
                    <AddToCartButton domainsCSV={presenter.name}>
                      {({ busy, onClick, inCart }) => (
                        <div className="tw-relative">
                          <button
                            className={cx(
                              'gb-btn gb-btn--primary gb-btn--block gb-btn--lg',
                              {
                                'tw-cursor-wait': busy,
                              },
                            )}
                            disabled={busy || inCart}
                            onClick={onClick}>
                            <div className="tw-flex tw-items-center tw-justify-center">
                              <i className="gb-icon gb-icon-morpheus-cart-add tw-mr-2" />
                              Add to cart
                            </div>
                          </button>
                          {inCart ? (
                            <div className="gb-btn gb-btn--sm tw-absolute tw-inset-0 tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-secondary-lighter tw-shadow-none tw-text-secondary–dark tw-pointer-events-none tw-rounded-none">
                              <div className="tw-flex tw-items-center tw-justify-center">
                                <i className="gb-icon gb-icon-morpheus-cart-success tw-mr-1" />
                                Added to Cart
                              </div>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </AddToCartButton>
                  </div>
                )}
              </div>
              {presenter.saleType === 'auction' && (
                <div className="tw-mt-8">
                  <div className="tw-mb-2 tw-text-gray-700 ">
                    <span className="tw-font-bold tw-uppercase">
                      Bid History
                    </span>
                    {presenter.bidCount > 2 && (
                      <span className="tw-ml-2 tw-text-sm tw-text-gray-600">
                        ({presenter.bidCount} bids, {presenter.bidderCount}{' '}
                        bidders)
                      </span>
                    )}
                  </div>
                  {!saleQuery.previousData && saleQuery.loading ? (
                    <Skeleton
                      className="tw-bg-gray-100 tw-rounded-lg"
                      height={100}
                      variant="rect"
                    />
                  ) : (
                    <table className="tw-w-full tw-rounded-lg tw-overflow-hidden tw-bg-white tw-text-sm">
                      <thead>
                        <tr className="tw-h-12 tw-text-white tw-text-left tw-bg-gray-500">
                          <th className="tw-px-3 tw-font-medium">Bidder</th>
                          <th className="tw-px-3 tw-font-medium tw-whitespace-nowrap">
                            Bid (USD)
                          </th>
                          <th className="tw-hidden sm:tw-table-cell tw-px-3 tw-font-medium tw-whitespace-nowrap">
                            Max Bid (USD)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {presenter.bids.length > 0 ? (
                          presenter.bids.map((bid, index) => {
                            return (
                              <tr
                                className={cx('tw-text-gray-700', {
                                  'tw-bg-gray-100':
                                    index % 2 !== presenter.bids.length % 2,
                                  'tw-bg-near-white':
                                    index % 2 === presenter.bids.length % 2,
                                  'tw-font-bold': bid.isLeadingBid,
                                })}
                                key={
                                  bid.user.displayName.value +
                                  bid.amount +
                                  bid.createdDate
                                }>
                                <td className="tw-p-3">
                                  <div className="tw-flex items-baseline">
                                    {bid.user.displayName.value}{' '}
                                    {bid.user.isSelf && '(you)'}
                                    {bid.status !== 'processed' && (
                                      <Badge className="tw-font-bold tw-ml-1 tw-bg-red-100 tw-text-red-500 tw-text-xs">
                                        Removed
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="tw-font-light tw-text-xs">
                                    {new Date(bid.createdDate).toLocaleString()}
                                  </div>
                                </td>
                                <td className="tw-p-3">
                                  {formatCurrency(bid.amount)}
                                  {bid.maxAmount && (
                                    <div className="sm:tw-hidden tw-font-light tw-text-xs">
                                      (Max bid: {formatCurrency(bid.maxAmount)})
                                    </div>
                                  )}
                                </td>
                                <td className="tw-hidden sm:tw-table-cell tw-p-3">
                                  {bid.maxAmount
                                    ? formatCurrency(bid.maxAmount)
                                    : '(hidden)'}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr className="tw-h-12">
                            <td
                              className="tw-text-center tw-text-sm tw-bg-gray-100"
                              colSpan="4">
                              No bids have been placed.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                  {presenter.bids.length >= saleQuery.variables.bidLimit && (
                    <button
                      className="gb-btn gb-btn--sm tw-mt-4"
                      onClick={() =>
                        saleQuery.refetch({
                          bidLimit: saleQuery.variables.bidLimit + bidLimit,
                        })
                      }
                      type="button">
                      Load More
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="tw-w-full lg:tw-w-1/2 lg:tw-pl-4 tw-mt-8 lg:tw-mt-0">
              <DomainMetrics
                presenter={presenter}
                loading={!saleQuery.previousData && saleQuery.loading}
              />
            </div>
          </div>
        </div>
      </div>
      <SaleCollection />
    </>
  );
}

const SaleCollection = () => {
  const collectionQuery = useQuery(queries.SalePageCollection, {
    variables: { pageSize: 20 },
  });
  if (collectionQuery.loading) {
    return null;
  }
  if (!collectionQuery.data?.collection?.total > 0) {
    return null;
  }
  return (
    <SaleCarousel
      className="tw-pb-12"
      heading="You might also like"
      sales={collectionQuery.data.collection.items}
      theme="secondary"
    />
  );
};

const SaleCountdown = ({ pause = false, to }) => {
  const countdown = useCountdown(to, pause);
  return (
    <div className="tw-inline-flex tw--mx-2 tw-mt-2 md:tw-mt-0 tw-text-gray-700">
      {Object.keys(countdown).map((key, index, items) => (
        <Fragment key={key}>
          <div className="tw-px-2 tw-text-center tw-font-medium">
            <div className="tw-flex tw-items-center tw-text-3xl">
              {countdown[key]}
            </div>
            <div className="tw-text-xs">{key}</div>
          </div>
          {index !== items.length - 1 && (
            <div className="tw-text-3xl tw-text-gray-400">:</div>
          )}
        </Fragment>
      ))}
    </div>
  );
};

const SaleBidForm = ({ sale }) => {
  const [showModal, setShowModal] = useState(false);
  const bidInputRef = useRef();
  const history = useHistory();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const [bidInputCache, setBidInputCache] = useLocalStorage(
    'SaleBidForm:bidInputCache',
    {},
  );
  const [createBid, createBidQuery] = useMutation(queries.CreateBid, {
    refetchQueries: ['SalePage'],
  });

  useWaitCursor(createBidQuery.loading);

  const onBidSubmit = async event => {
    event.preventDefault();
    const amount = Number(bidInputCache[sale.name]?.replace(/,/g, '') ?? 0);
    if (amount < sale.minBid) {
      bidInputRef.current.setCustomValidity(
        `The minimum bid amount is ${sale.minBidFormatted} (USD)`,
      );
      bidInputRef.current.reportValidity();
      return;
    }
    try {
      const result = await createBid({
        variables: { amount, sale: sale.id },
      });
      if (result.data?.createBid) {
        const { isLeadingBid } = result.data.createBid.bid;
        document.dispatchEvent(
          new CustomEvent(AppDialog.events.open, {
            detail: {
              key: isLeadingBid ? 'bidSuccessLeading' : 'bidSuccessOutbid',
              bid: result.data.createBid.bid,
              sale: getSalePresenter(result.data.createBid.sale),
            },
          }),
        );
      }
      setBidInputCache({
        ...bidInputCache,
        [sale.name]: undefined,
      });
      if (showModal) setShowModal(false);
    } catch (error) {
      if (isAuthError(error)) {
        const params = new URLSearchParams(location.search);
        params.set('loginSuccess', '1');
        window.location = getAuthRedirectUrl(
          `${location.pathname}?${params.toString()}`,
        );
      } else if (
        error?.graphQLErrors?.find(
          e => e.extensions.code === 'SUBSCRIPTION_NOT_FOUND',
        )
      ) {
        document.dispatchEvent(
          new CustomEvent(AppDialog.events.open, {
            detail: {
              key: 'subscriptionDisclosure',
              returnTo: `/sale/${sale.id}`,
            },
          }),
        );
      }
    }
  };

  useEffect(() => {
    if (searchParams.has('loginSuccess')) {
      setShowModal(true);
      searchParams.delete('loginSuccess');
      history.replace({
        ...location,
        search: searchParams.toString(),
      });
    }
  }, [searchParams, location, history]);

  const content = (
    <>
      {createBidQuery.error &&
      createBidQuery.error.graphQLErrors?.[0]?.extensions?.code !==
        'UNAUTHENTICATED' ? (
        <div className="tw-mt-6">
          <div className="gb-alert gb-alert--error">
            <div className="gb-alert__body">
              <div className="gb-alert__icon">
                <i className="gb-icon gb-icon-morpheus-error-full" />
              </div>
              <div className="gb-alert__content">
                <div className="gb-alert__title">
                  {createBidQuery.error.message || 'Failed to place bid'}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {sale.status === 'active' && (
        <form className="tw-mt-6" onSubmit={onBidSubmit}>
          <div className="tw-flex tw-flex-wrap tw-justify-end tw-max-w-xl">
            <TextInput
              className="tw-flex-auto tw-w-full sm:tw-w-auto tw-mb-6 tw-rounded"
              inputClassName="tw-pl-6"
              innerLeft={<div className="tw-pl-3 tw-font-light">$</div>}
              inputMode="numeric"
              min={sale.price}
              onChange={event => {
                bidInputRef.current.setCustomValidity('');
                bidInputRef.current.reportValidity();
                setBidInputCache({
                  ...bidInputCache,
                  [sale.name]: event.target.value,
                });
              }}
              pattern="[0-9\.,]*"
              ref={bidInputRef}
              title="Valid currency in USD"
              type="text"
              value={bidInputCache[sale.name] ?? ''}
            />
            <div className="tw-mb-3 sm:tw-w-full tw-flex tw-flex-auto sm:tw-order-1">
              <div
                className={cx('tw-relative tw-flex-auto', {
                  'tw-pb-12': Boolean(sale.renewPriceFormatted),
                })}>
                <div className="gb-checkbox gb-checkbox--sm">
                  <input
                    className="gb-checkbox__input"
                    id="payment-agreement"
                    required
                    type="checkbox"
                  />
                  <label
                    className="gb-checkbox__label tw-cursor-pointer tw-leading-relaxed"
                    htmlFor="payment-agreement">
                    I agree to pay if I win this domain
                  </label>
                </div>
                {sale.renewPriceFormatted && (
                  <div className="tw-absolute tw-w-full tw-text-xs md:tw-text-sm tw-leading-snug tw-pl-6">
                    An additional fee of {sale.renewPriceFormatted} will be
                    added to the final purchase price for one year of
                    registration for .{sale.tld} domains.
                  </div>
                )}
              </div>
            </div>
            <button
              className="gb-btn gb-btn--primary gb-btn--sm tw-ml-5"
              disabled={createBidQuery.loading}
              type="submit">
              Place Max Bid
            </button>
          </div>
        </form>
      )}
    </>
  );

  if (showModal) {
    return (
      <Modal onClose={() => setShowModal(false)} open>
        <ModalTitle>Complete your bid on {sale.name}</ModalTitle>
        {content}
      </Modal>
    );
  }

  return content;
};
